import { Form, Input, Select, Tooltip } from 'antd';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { PlusButton } from 'src/components/common/PlusButton/PlusButton';
import { SearchPanel } from 'src/components/SearchPanel/SearchPanel';
import {
    SearchPanelFilterForm,
    SearchPanelFilterFormProps,
} from 'src/components/SearchPanel/SearchPanelFilterForm';
import { EntityLinks } from 'src/core/router/EntityLinks';
import { Router } from 'src/core/router/Router';
import {
    CampaignsFilter,
    CampaignsStore,
} from 'src/stores/CampaignsStore/CampaignsStore';
import { UserStore } from 'src/stores/UserStore/UserStore';
import { FormHelper } from 'src/utils/FormHelper';
import styled from 'styled-components';

export const CampaignsPageHeaderPanel = observer(() => {
    const { t } = useTranslation();

    return (
        <StyledHeaderWrapper>
            <SearchPanel
                inputSearchFormName={formNames.getPath('filterSearch')}
                store={store}
                renderPanel={(props) => <FilterPanel {...props} />}
                inputSearchPlaceholder={t(
                    'Page.Campaigns.AsideHeader.Search.Placeholder',
                )}
            />
            {UserStore.can.manage.products && (
                <Tooltip
                    title={t('Page.Campaign.AsideHeader.CreateNewItem.Tooltip')}
                >
                    <PlusButton
                        onClick={() => {
                            const createLink = EntityLinks.campaigns.create?.();
                            createLink && Router.navigate(createLink);
                        }}
                        data-testid="header-panel-create-btn"
                    />
                </Tooltip>
            )}
        </StyledHeaderWrapper>
    );
});

const StyledHeaderWrapper = styled.header`
    position: relative;
    display: grid;
    grid-template-columns: 1fr auto;
`;

const FilterPanel = observer((props: SearchPanelFilterFormProps) => {
    const { t } = useTranslation();

    return (
        <SearchPanelFilterForm
            {...props}
            onFinish={(values: CampaignsFilter) => {
                store.pager.reset();
                store.filterCriteria?.applyFilter(
                    convertFormValuesToFilterCriteria(values),
                );
                store.loadList();
            }}
        >
            <StyledSelect
                label={t('Page.Products.Filter.Status')}
                name={formNames.getPath('filterActive')}
            >
                <Select allowClear data-testid="filterActive">
                    <Select.Option value="true" data-testid="true">
                        {t(`data:status.product.ACTIVE`)}
                    </Select.Option>
                    <Select.Option value="false" data-testid="false">
                        {t(`data:status.product.INACTIVE`)}
                    </Select.Option>
                </Select>
            </StyledSelect>
            <Form.Item
                label={t('Page.Products.Filter.MaxAmount')}
                name={formNames.getPath('filterMaxAmount')}
            >
                <StyledInput type="number" data-testid="filterMaxAmount" />
            </Form.Item>
            <Form.Item
                label={t('Page.Products.Filter.MaxTerm')}
                name={formNames.getPath('filterMaxTerm')}
            >
                <StyledInput type="number" data-testid="filterMaxTerm" />
            </Form.Item>
        </SearchPanelFilterForm>
    );
});

const store = CampaignsStore;
const formNames = FormHelper.createNameProvider<CampaignsFilter>();

const convertFormValuesToFilterCriteria = (
    values: CampaignsFilter,
): CampaignsFilter => {
    return values;
};

const StyledSelect = styled(Form.Item)`
    .ant-select:not(.ant-select-customize-input) .ant-select-selector {
        display: flex;
        align-items: center;
        height: 2.7rem;
        border-radius: 0.5rem;
    }
`;

const StyledInput = styled(Input)`
    height: 2.7rem;
    border-radius: 0.5rem;
`;
